import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useConfiguration } from '@gov-nx/core/service/configuration/useConfiguration';
import { useFormBuilder } from '@gov-nx/core/service/forms/PoForm';
import type { UncheckedKey } from '@gov-nx/core/service/translations/translation.types';
import { useLocale } from '@gov-nx/core/service/translations/useLocale';
import type { Nullable } from '@gov-nx/core/types/helpers.types';
import { is } from '@gov-nx/core/types/helpers.types';
import type { PageCode } from '@gov-nx/module/page/definitions/codes';
import { getRadioDefinition } from '@gov-nx/utils/common/Form/radio';
import { useInputDefinition } from '@gov-nx/utils/common/Form/string';
import { useOnChange } from '@gov-nx/utils/common/Hooks/useOnChange';
import { useAutorizaceContext } from './Context';
import type { AuthorizationForm, AuthorizationFormFields } from './form.types';
import { AUTHORIZATION_METHOD_COPY, RESEND_COUNTDOWN_SECONDS } from './methods';
import { AuthorizationType } from './types';
import { buildIdentityUdajeUrl } from './utils';

const OPTION_ORDER: AuthorizationType[] = [
	AuthorizationType.SMS,
	AuthorizationType.FCM,
	AuthorizationType.AUTENTIZACE,
];

/**
 * Per-method resend cooldown. Lifted out of MethodPanel so it survives the
 * selected panel unmounting on a method switch — keeping it in the panel let a
 * user reset the 15s throttle just by toggling methods. Keyed by authorization
 * method: the cooldown (re)starts for the method that initiated a send only when
 * that send actually succeeds (`sendSucceeded`, i.e. sendAuthCodeQuery.isSuccess,
 * false → true). Gating on the raw loading flag instead would arm the throttle
 * after a failed (re)send too, blocking the user from retrying even though no new
 * code was delivered. Every active method ticks down on a single shared timer.
 */
const useResendCountdowns = (
	activeSendType: Nullable<AuthorizationType>,
	sendSucceeded: boolean
): Partial<Record<AuthorizationType, number>> => {
	const [secondsByMethod, setSecondsByMethod] = useState<
		Partial<Record<AuthorizationType, number>>
	>({});

	useOnChange(sendSucceeded, (succeeded, wasSucceeded) => {
		if (!wasSucceeded && succeeded && is(activeSendType)) {
			setSecondsByMethod((prev) => ({
				...prev,
				[activeSendType]: RESEND_COUNTDOWN_SECONDS,
			}));
		}
	});

	useEffect(() => {
		const hasActive = Object.values(secondsByMethod).some((s) => s > 0);
		if (!hasActive) {
			return;
		}
		const id = setTimeout(() => {
			setSecondsByMethod((prev) => {
				const next: Partial<Record<AuthorizationType, number>> = {};
				for (const method of Object.keys(prev) as AuthorizationType[]) {
					next[method] = Math.max(0, (prev[method] ?? 0) - 1);
				}
				return next;
			});
		}, 1000);
		return () => clearTimeout(id);
	}, [secondsByMethod]);

	return secondsByMethod;
};

interface UseAuthorizationMethodsFormProps {
	code: (typeof PageCode)['autorizace'];
	allowedAuthorizationTypes: AuthorizationType[];
	onRedirectAuthorization: (ukonId: string) => void;
}

export const useAuthorizationMethodsForm = ({
	code,
	allowedAuthorizationTypes,
	onRedirectAuthorization,
}: UseAuthorizationMethodsFormProps) => {
	const { identitaObcanaUrl } = useConfiguration();
	const authContext = useAutorizaceContext();
	const { t } = useLocale(code);

	const submitting = !!authContext?.confirmAuthCode.isLoading;

	const codeInputDefinition = useInputDefinition({
		name: 'oneTimePassword',
		label: t('code-input.label'),
		placeholder: '',
		isRequired: true,
		isDisabled: submitting,
	});

	const options = OPTION_ORDER.map((type) => ({
		name: type,
		label: t<UncheckedKey>(`authorization-type.${type.toLowerCase()}.label`),
	})).filter((option) => allowedAuthorizationTypes.includes(option.name));

	const radioDefinition = getRadioDefinition<AuthorizationType>({
		name: 'authorizationType',
		label: t('dialog.methods-label'),
		defaultValue: options[0]?.name ?? null,
		options,
	});

	const form = useFormBuilder<AuthorizationForm, AuthorizationFormFields>([
		radioDefinition,
		codeInputDefinition,
	]);

	const selected = useWatch({
		control: form.formMethods.control,
		name: 'authorizationType',
	}) as Nullable<AuthorizationType>;

	// sendAuthCodeQuery is shared across methods, so its loading/success flags
	// are only meaningful for the method that actually initiated the send.
	// variables holds the last mutate() args (see Context.onNextStep), whose
	// body.kanal is the initiating channel. Guarding on it prevents a late
	// success from a previously selected method (e.g. SMS) from rendering the
	// newly selected method's panel (e.g. FCM) as if its code had been sent —
	// a real OTP-channel mix-up.
	const activeSendType = authContext?.sendAuthCodeQuery.variables?.body
		?.kanal as Nullable<AuthorizationType>;
	const sending =
		!!authContext?.sendAuthCodeQuery.isLoading && activeSendType === selected;

	// Latch, per method, whether a code was ever successfully sent. Deriving the
	// confirm UI straight from sendAuthCodeQuery.isSuccess would collapse it the
	// moment a resend re-enters loading (isSuccess flips back to false) and keep it
	// collapsed after a resend failure — stranding the user without the OTP input
	// even though the already-delivered code is still valid. The latch keeps the
	// confirm UI up once a send has succeeded, regardless of later resend attempts.
	const [sentMethods, setSentMethods] = useState<Set<AuthorizationType>>(
		() => new Set()
	);
	useEffect(() => {
		if (authContext?.sendAuthCodeQuery.isSuccess && is(activeSendType)) {
			setSentMethods((prev) => {
				if (prev.has(activeSendType)) {
					return prev;
				}
				const next = new Set(prev);
				next.add(activeSendType);
				return next;
			});
		}
	}, [authContext?.sendAuthCodeQuery.isSuccess, activeSendType]);

	const codeSent = is(selected) && sentMethods.has(selected);

	// Cooldown is keyed on the initiating channel (activeSendType) and armed on a
	// successful send only. react-query resets isSuccess to false whenever a resend
	// re-enters loading (the same behaviour the sentMethods latch above depends on),
	// so isSuccess flips false → true once per successful (re)send — including for a
	// method the user has already switched away from, which still throttles the
	// channel it belongs to. A failed resend leaves isSuccess false, so it never
	// arms the cooldown.
	const resendCountdowns = useResendCountdowns(
		activeSendType,
		!!authContext?.sendAuthCodeQuery.isSuccess
	);
	const resendSecondsLeft = is(selected)
		? (resendCountdowns[selected] ?? 0)
		: 0;

	// Clear the entered code when switching methods. The per-method sent-code latch
	// above is intentionally preserved, so returning to a method that already
	// received a code still shows its confirm UI rather than the send button.
	useEffect(() => {
		form.formMethods.resetField('oneTimePassword');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selected]);

	// The shared OTP input takes its placeholder from the selected method.
	const placeholderKey = selected
		? AUTHORIZATION_METHOD_COPY[selected].placeholderKey
		: undefined;
	if (placeholderKey) {
		form.fields.oneTimePassword.field.placeholder =
			t<UncheckedKey>(placeholderKey);
	}

	// Freeze method selection while an OTP confirmation is in flight, mirroring
	// the old wizard (its method-selection step was `disabled` during
	// confirmAuthCode.isLoading). Otherwise the user could switch methods —
	// triggering another send — while the previous method's confirm is pending.
	form.fields.authorizationType.field.isDisabled = submitting;

	const onAction = (type: AuthorizationType) => {
		if (!is(authContext)) {
			return;
		}
		if (type === AuthorizationType.AUTENTIZACE) {
			onRedirectAuthorization(authContext.ukonId);
			return;
		}
		authContext.onNextStep(type);
	};

	return {
		form,
		selected,
		identityUrl: buildIdentityUdajeUrl(identitaObcanaUrl),
		codeSent,
		sending,
		submitting,
		resendSecondsLeft,
		onAction,
		handleConfirmSubmit: form.formMethods.handleSubmit(
			(data: AuthorizationForm) => authContext?.onConfirmSubmit(data)
		),
	};
};
